import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Checkbox, Divider, FormControlLabel, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Box } from "@mui/system";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { type FC, type MouseEvent, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { SystemDatePickerPopup } from "../../components/UIToolkit/Popups/SystemDatePickerPopup";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { CensusOverTime } from "./CensusOverTime";
import { PatientTable } from "./PatientsTable";
import { getMockPatientsForDate } from "./utils";

export const Patients: FC = () => {
	const { t } = useTranslation();
	const { dateString } = useParams();
	const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);
	const [graphExpanded, setGraphExpanded] = useState<boolean>(true);
	const [hidePatientNames, setHidePatientNames] = useState<boolean>(false);
	const {
		chosenDepartment,
		hospitalData,
		getWeekIndexByDate,
		getDayIndexByDate,
		timezone,
		latestDateInWeeksData,
		isHospitalDemo,
		isHospitalView,
		isSnfDemo,
		isAlfDemo,
	} = useHospitalData();
	const showHospitalView = isHospitalView || isHospitalDemo;
	// Redesign shows only on hospital demos (Hospital type + inhouse2).
	const isRealHospitalDemo = isHospitalView;
	const navigate = useNavigate();
	const lastNameFirst = useLastNameFirst();
	const today = DateTime.now().setZone(timezone);
	const date = (dateString ? DateTime.fromISO(dateString) : DateTime.now()).setZone(timezone);
	const minimumDate = DateTime.min(today, ...(hospitalData.weeks[0]?.weeklyShifts.flatMap((weekData) => weekData.days[0]?.date) ?? []));
	const { census, patientsData } = useMemo(
		() =>
			getMockPatientsForDate({
				hospitalData,
				getWeekIndexByDate,
				getDayIndexByDate,
				date,
				isHospitalDemo: showHospitalView,
				lastNameFirst,
			}),
		[hospitalData, getDayIndexByDate, getWeekIndexByDate, date, showHospitalView, lastNameFirst],
	);
	const toggleDatePicker = (event: MouseEvent<HTMLButtonElement>) => {
		setDatePickerAnchorEl((prevState) => (prevState ? null : event.currentTarget));
	};
	const handleDateChange = useCallback(
		(newDate: DateTime | null) => {
			if (newDate?.isValid) {
				// The route is /:departmentId/patients/:dateString — without the department id no route
				// matches and picking a date lands on the 404 page.
				navigate(`/${chosenDepartment.department_id}/patients/${newDate.toFormat("yyyy-MM-dd")}`);
			}
		},
		[navigate, chosenDepartment.department_id],
	);
	const onClosePicker = () => setDatePickerAnchorEl(null);
	const toggleGraphExpanded = () => {
		setGraphExpanded((prevState) => !prevState);
	};
	const toggleHidePatientNames = () => {
		setHidePatientNames((prevState) => !prevState);
	};
	const isDemo = showHospitalView || isSnfDemo || isAlfDemo;

	const headerRef = useRef<HTMLElement | null>(null);
	const [headerHeight, setHeaderHeight] = useState(0);

	useLayoutEffect(() => {
		if (!headerRef.current) return;
		setHeaderHeight(headerRef.current.clientHeight);
	}, []);

	return (
		<Box
			data-hospital-demo={isRealHospitalDemo ? "true" : undefined}
			sx={{
				width: "100%",
				height: "100%",
				p: 2,
				// Demo drops the tinted background; others keep the primary wash. (DM Sans is applied
				// app-wide for the demo via HospitalDemoThemeProvider — no per-page font override needed.)
				bgcolor: (theme) => (isRealHospitalDemo ? "transparent" : alpha(theme.palette.primary.main, 0.08)),
				display: "flex",
				flexDirection: "column",
				gap: 1,
				overflowX: "clip",
			}}
		>
			<Box
				ref={headerRef}
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					width: "100%",
				}}
			>
				{datePickerAnchorEl && (
					<SystemDatePickerPopup
						overridePickerProps={{
							minDate: minimumDate,
							maxDate: latestDateInWeeksData,
						}}
						value={date}
						onChange={handleDateChange}
						anchorEl={datePickerAnchorEl}
						open={!!datePickerAnchorEl}
						onClose={onClosePicker}
					/>
				)}
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "baseline",
						gap: 2,
					}}
				>
					<Typography
						sx={{
							color: "primary.main",
							fontSize: "26px",
							lineHeight: "100%",
							fontWeight: 700,
						}}
					>
						Patients
					</Typography>
					<Divider
						orientation="vertical"
						variant="fullWidth"
						flexItem
						sx={{
							backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.5),
						}}
					/>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							color: "primary.main",
						}}
					>
						<Typography
							sx={{
								fontSize: "18px",
								lineHeight: "100%",
								fontWeight: 500,
								color: "inherit",
							}}
						>
							{date.toFormat("MMM d, yyyy")}
						</Typography>
						<IconButton onClick={toggleDatePicker} size={"small"} color={"inherit"}>
							{datePickerAnchorEl ? <ArrowUpwardIcon color={"inherit"} fontSize={"inherit"} /> : <ArrowDownwardIcon color={"inherit"} fontSize={"inherit"} />}
						</IconButton>
					</Box>
					<Divider
						orientation="vertical"
						variant="fullWidth"
						flexItem
						sx={{
							backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.5),
						}}
					/>
					<Typography
						sx={{
							color: "primary.dark",
							fontSize: "18px",
							lineHeight: "20px",
							fontWeight: 500,
						}}
					>
						<span
							style={{
								fontWeight: 400,
								// Raw DOM style — MUI sx string tokens ("primary.main") don't resolve
								// here, so consume the published brand CSS variable instead.
								color: "var(--inhouse-primary)",
								paddingRight: 6,
							}}
						>
							Expected Census
						</span>
						{census - 1}-{census + 1}
					</Typography>
					{isDemo && (
						<>
							<Divider
								orientation="vertical"
								variant="fullWidth"
								flexItem
								sx={{
									backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.5),
								}}
							/>
							{isRealHospitalDemo ? (
								// Demo: a "Display names" checkbox (checked = names shown) instead of the eye toggle.
								<FormControlLabel
									control={<Checkbox checked={!hidePatientNames} onChange={toggleHidePatientNames} size={"small"} sx={{ color: "primary.main", py: 0 }} />}
									label={t("Display names")}
									sx={{ m: 0, color: "primary.main", "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
								/>
							) : (
								<IconButton onClick={toggleHidePatientNames} size={"small"} sx={{ color: "primary.main" }}>
									{hidePatientNames ? <VisibilityOff color={"inherit"} fontSize={"inherit"} /> : <Visibility color={"inherit"} fontSize={"inherit"} />}
								</IconButton>
							)}
						</>
					)}
				</Box>

				<Divider
					sx={{
						backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
					}}
				/>
			</Box>
			<Box
				sx={{
					flex: 1,
					height: `calc(100% - ${headerHeight}px - 4px)`,
					display: "flex",
					gap: 1,
				}}
			>
				<PatientTable
					width={{
						xs: graphExpanded ? "50%" : "65%",
						xxl: graphExpanded ? "40%" : "65%",
					}}
					height={"100%"}
					patientsData={patientsData}
					hidePatientNames={hidePatientNames}
					isHospitalDemo={showHospitalView}
					isRealHospitalDemo={isRealHospitalDemo}
				/>
				<Box
					sx={{
						height: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<IconButton size={"medium"} onClick={toggleGraphExpanded}>
						{graphExpanded ? <ArrowRightIcon fontSize={"inherit"} /> : <ArrowLeftIcon fontSize={"inherit"} />}
					</IconButton>
				</Box>
				<CensusOverTime date={date} lastDateInWeeksData={latestDateInWeeksData} />
			</Box>
		</Box>
	);
};
